import { useState, useEffect } from 'react';
import { Eye, Trash2, X, Mail, Phone, Calendar, MessageSquare, User, Search, CheckCircle, Clock, ChevronRight, Inbox, Star, Filter } from 'lucide-react';
import { databaseService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

const CONTACT_RESPONSES_COLLECTION = 'contact_responses';

export default function ContactResponses() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all, unread, read
  const { showToast } = useToast();

  useEffect(() => {
    loadResponses();
  }, []);

  const loadResponses = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(CONTACT_RESPONSES_COLLECTION);
      
      if (result.success) {
        // Sort by date, newest first
        const sorted = result.data.documents.sort((a, b) => 
          new Date(b.$createdAt) - new Date(a.$createdAt)
        );
        setResponses(sorted);
        // Select the first one if available
        if (sorted.length > 0 && !selectedResponse) {
          // don't mark as read immediately to avoid clutter
        }
      }
    } catch (error) {
      console.error('Error loading responses:', error);
      showToast('Failed to load responses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure? This message will be permanently deleted.')) return;

    try {
      const result = await databaseService.deleteDocument(
        CONTACT_RESPONSES_COLLECTION,
        id
      );

      if (result.success) {
        showToast('Conversation deleted', 'success');
        setResponses(prev => prev.filter(r => r.$id !== id));
        if (selectedResponse?.$id === id) {
          setSelectedResponse(null);
        }
      }
    } catch (error) {
      showToast('Failed to delete', 'error');
    }
  };

  const markAsRead = async (response) => {
    if (response.isRead) return;

    try {
      await databaseService.updateDocument(
        CONTACT_RESPONSES_COLLECTION,
        response.$id,
        { isRead: true }
      );
      
      setResponses(prev => prev.map(r => 
        r.$id === response.$id ? { ...r, isRead: true } : r
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSelectResponse = (response) => {
    setSelectedResponse(response);
    if (!response.isRead) markAsRead(response);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (days < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatFullDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
      });
  };

  // Filter Logic
  const filteredResponses = responses.filter(r => {
    const matchesSearch = 
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      r.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filter === 'unread') return matchesSearch && !r.isRead;
    if (filter === 'read') return matchesSearch && r.isRead;
    return matchesSearch;
  });

  const unreadCount = responses.filter(r => !r.isRead).length;

  return (
    <div className="flex h-[calc(100vh-100px)] -m-6 bg-[#F8F9FC]">
      {/* Left Sidebar: Inbox List */}
      <div className={`w-full md:w-[400px] flex flex-col border-r bg-white ${selectedResponse ? 'hidden md:flex' : 'flex'}`}>
         {/* Inbox Header */}
         <div className="p-5 border-b bg-white">
            <h1 className="text-2xl font-bold text-[#4C1D95] mb-4 flex items-center gap-2">
                <Inbox className="w-6 h-6" /> Inbox 
                {unreadCount > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>}
            </h1>
            
            {/* Search */}
            <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input 
                    type="text" 
                    placeholder="Search messages..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#4C1D95] focus:border-transparent outline-none transition-all"
                />
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 text-xs font-semibold overflow-x-auto no-scrollbar">
                {['all', 'unread', 'read'].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-1.5 rounded-full capitalize transition-colors ${
                            filter === f 
                            ? 'bg-[#4C1D95] text-white' 
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                    >
                        {f}
                    </button>
                ))}
            </div>
         </div>

         {/* Message List */}
         <div className="flex-1 overflow-y-auto">
            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4C1D95]"></div>
                </div>
            ) : filteredResponses.length === 0 ? (
                <div className="text-center py-16 px-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-8 h-8 text-gray-300" />
                    </div>
                    <h3 className="text-gray-500 font-medium">No messages found</h3>
                    <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                </div>
            ) : (
                <div className="divide-y divide-gray-50">
                    {filteredResponses.map((response) => (
                        <div 
                            key={response.$id}
                            onClick={() => handleSelectResponse(response)}
                            className={`p-4 cursor-pointer transition-all hover:bg-gray-50 relative group ${
                                selectedResponse?.$id === response.$id ? 'bg-purple-50' : 
                                !response.isRead ? 'bg-white' : 'bg-white opacity-90'
                            }`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <h3 className={`font-semibold text-sm truncate pr-2 ${!response.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                                    {response.name}
                                </h3>
                                <span className={`text-[10px] whitespace-nowrap ${!response.isRead ? 'text-[#4C1D95] font-bold' : 'text-gray-400'}`}>
                                    {formatDate(response.$createdAt)}
                                </span>
                            </div>
                            <h4 className={`text-xs mb-1 truncate ${!response.isRead ? 'font-medium text-gray-800' : 'text-gray-500'}`}>
                                {response.subject}
                            </h4>
                            <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                                {response.message}
                            </p>
                            
                            {!response.isRead && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4C1D95]"></div>
                            )}

                            {/* Hover Actions */}
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex bg-white/90 backdrop-blur rounded-lg shadow-sm border p-1">
                                <button
                                    onClick={(e) => handleDelete(response.$id, e)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
         </div>
      </div>

      {/* Right Pane: Message Detail */}
      <div className={`flex-1 flex flex-col bg-[#F8F9FC] ${!selectedResponse ? 'hidden md:flex' : 'flex'}`}>
         {selectedResponse ? (
             <div className="h-full flex flex-col">
                 {/* Detail Header */}
                 <div className="bg-white p-6 border-b flex justify-between items-start shadow-sm z-10">
                     <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-3 mb-4 md:hidden">
                            <button onClick={() => setSelectedResponse(null)} className="p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-full">
                                <ChevronRight className="w-5 h-5 rotate-180" />
                            </button>
                            <span className="text-sm font-semibold text-gray-500">Back to Inbox</span>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">{selectedResponse.subject}</h2>
                        
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm ${
                                ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500'][selectedResponse.name.length % 4]
                            }`}>
                                {selectedResponse.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-sm text-gray-900">{selectedResponse.name}</span>
                                    <span className="text-xs text-gray-400">&lt;{selectedResponse.email}&gt;</span>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> {formatFullDate(selectedResponse.$createdAt)}
                                    </span>
                                </div>
                            </div>
                        </div>
                     </div>
                     
                     <div className="flex items-center gap-2">
                         <a 
                            href={`mailto:${selectedResponse.email}?subject=Re: ${selectedResponse.subject}`}
                            className="px-4 py-2 bg-[#4C1D95] text-white text-sm font-bold rounded-lg hover:bg-[#3b1575] transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
                         >
                            <Mail className="w-4 h-4" /> Reply
                         </a>
                         <button 
                            onClick={(e) => handleDelete(selectedResponse.$id, e)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                            title="Delete Conversation"
                         >
                            <Trash2 className="w-5 h-5" />
                         </button>
                     </div>
                 </div>

                 {/* Detail Body */}
                 <div className="flex-1 overflow-y-auto p-6 md:p-10">
                     <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-4xl mx-auto">
                        <div className="prose prose-purple max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {selectedResponse.message}
                        </div>
                     </div>

                     {/* Sender Details Card */}
                     <div className="mt-8 max-w-4xl mx-auto grid grid-cols-1 gap-4">
                        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                                <Mail className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-bold text-gray-400 uppercase">Email</p>
                                <p className="text-sm font-medium text-gray-900 break-all">{selectedResponse.email}</p>
                            </div>
                        </div>
                        
                        {selectedResponse.phone && (
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-600">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase">Phone</p>
                                    <p className="text-sm font-medium text-gray-900">{selectedResponse.phone}</p>
                                </div>
                            </div>
                        )}
                        
                        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-[#4C1D95]">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-gray-400 uppercase">Received</p>
                                <p className="text-sm font-medium text-gray-900">{formatDate(selectedResponse.$createdAt)}</p>
                            </div>
                        </div>
                     </div>
                 </div>
             </div>
         ) : (
             /* Empty State for Detail Pane */
             <div className="h-full flex flex-col items-center justify-center text-gray-400 p-6">
                 <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
                     <Mail className="w-10 h-10 text-gray-300" />
                 </div>
                 <h2 className="text-2xl font-bold text-gray-700 mb-2">Select a message</h2>
                 <p className="text-center max-w-xs text-gray-500">Choose a conversation from the list to view details, reply, or manage.</p>
             </div>
         )}
      </div>
    </div>
  );
}
